import { useEffect, useRef, useState } from "react";
import { Mic, Square, Play, Pause, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";

const formatTime = (s) => {
  const mm = Math.floor(s / 60)
    .toString()
    .padStart(2, "0");
  const ss = Math.floor(s % 60)
    .toString()
    .padStart(2, "0");
  return `${mm}:${ss}`;
};

/**
 * VoiceRecorder
 * - Records audio via MediaRecorder
 * - Shows pulsing button + simple waveform + timer
 * - Exposes `audioFile` (File) via onReady callback
 */
export default function VoiceRecorder({ onAudioReady, disabled = false }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [audioURL, setAudioURL] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [playing, setPlaying] = useState(false);

  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      stopStream();
      if (timerRef.current) clearInterval(timerRef.current);
      if (audioURL) URL.revokeObjectURL(audioURL);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const startRecording = async () => {
    if (disabled) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;

      const mimeCandidates = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg;codecs=opus",
      ];
      const supported = mimeCandidates.find(
        (m) => window.MediaRecorder && MediaRecorder.isTypeSupported(m)
      );
      const mr = new MediaRecorder(stream, supported ? { mimeType: supported } : {});
      mediaRecorderRef.current = mr;
      chunksRef.current = [];

      mr.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      mr.onstop = () => {
        const type = mr.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const ext = type.includes("mp4")
          ? "mp4"
          : type.includes("ogg")
          ? "ogg"
          : "webm";
        const file = new File([blob], `recording.${ext}`, { type });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        setAudioFile(file);
        onAudioReady?.(file);
        stopStream();
      };

      mr.start(200);
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (err) {
      console.error(err);
      toast.error(
        "Microphone access blocked. Please allow mic permission or upload an audio file instead."
      );
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setRecording(false);
  };

  const discard = () => {
    if (audioURL) URL.revokeObjectURL(audioURL);
    setAudioURL(null);
    setAudioFile(null);
    setElapsed(0);
    setPlaying(false);
    onAudioReady?.(null);
  };

  const togglePlay = () => {
    if (!audioRef.current) return;
    if (playing) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
  };

  return (
    <div
      className="bg-white border border-sage rounded-2xl shadow-ambient p-6 sm:p-8"
      data-testid="voice-recorder"
    >
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="accent-label mb-1">Step 01</p>
          <h3 className="font-display text-xl sm:text-2xl text-ink">
            Tap the mic & describe the issue
          </h3>
        </div>
      </div>

      <div className="flex flex-col items-center text-center py-4 sm:py-6">
        <button
          type="button"
          data-testid={recording ? "stop-recording-button" : "start-recording-button"}
          onClick={recording ? stopRecording : startRecording}
          disabled={disabled || !!audioFile}
          aria-label={recording ? "Stop recording" : "Start recording"}
          className={[
            "relative grid place-items-center rounded-full transition-all duration-300",
            "h-28 w-28 sm:h-32 sm:w-32",
            recording
              ? "bg-terracotta text-white animate-mic-pulse"
              : audioFile
              ? "bg-sand text-[#7B9672] cursor-not-allowed"
              : "bg-white text-terracotta border-2 border-[#D47A6A]/25 hover:border-[#D47A6A] hover:shadow-lift hover:-translate-y-0.5",
            "disabled:opacity-60",
          ].join(" ")}
        >
          {recording ? (
            <Square className="w-9 h-9 fill-current" strokeWidth={1.5} />
          ) : (
            <Mic className="w-10 h-10" strokeWidth={1.5} />
          )}
        </button>

        <div
          className="mt-6 font-display text-3xl text-ink tabular-nums"
          data-testid="recording-timer"
        >
          {formatTime(elapsed)}
        </div>

        {/* Waveform bars */}
        <div className="mt-4 flex items-end gap-1 h-10">
          {Array.from({ length: 16 }).map((_, i) => (
            <span
              key={i}
              className={[
                "w-1.5 rounded-full",
                recording ? "bg-terracotta waveform-bar" : "bg-[#E2E8DE]",
              ].join(" ")}
              style={{
                height: "100%",
                animationDelay: `${(i % 8) * 80}ms`,
              }}
            />
          ))}
        </div>

        <p className="mt-5 text-sm text-ink-muted max-w-md">
          {recording
            ? "Recording... speak naturally. Tap the red button when you're done."
            : audioFile
            ? "Recording captured. You can play it back, re-record, or continue below."
            : "Press the mic, then describe what you were trying to do, what went wrong, and when it happened."}
        </p>
      </div>

      {audioFile && (
        <div
          className="mt-6 flex items-center justify-between gap-4 rounded-xl bg-sand px-4 py-3"
          data-testid="recorded-audio-preview"
        >
          <div className="flex items-center gap-3 min-w-0">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={togglePlay}
              data-testid="play-recorded-audio"
              className="h-10 w-10 rounded-full bg-white hover:bg-white text-ink border border-sage"
            >
              {playing ? (
                <Pause className="w-4 h-4" strokeWidth={1.8} />
              ) : (
                <Play className="w-4 h-4" strokeWidth={1.8} />
              )}
            </Button>
            <div className="min-w-0">
              <p className="text-sm font-medium text-ink truncate">
                Your recording · {formatTime(elapsed)}
              </p>
              <p className="text-xs text-ink-muted truncate">{audioFile.name}</p>
            </div>
            <audio
              ref={audioRef}
              src={audioURL}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
            />
          </div>
          <Button
            type="button"
            variant="ghost"
            onClick={discard}
            data-testid="discard-recording-button"
            className="rounded-xl text-[#A85346] hover:bg-[#D47A6A]/10 hover:text-[#A85346]"
          >
            <Trash2 className="w-4 h-4 mr-1.5" strokeWidth={1.6} />
            Re-record
          </Button>
        </div>
      )}
    </div>
  );
}
